import React, { useState, useEffect, ChangeEvent, ReactNode, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import heic2any from 'heic2any';
import { FileService } from './service';
import { convertFileToBase64 } from './functions';
import { urlFile } from '../../AxiosConfig';

const service = new FileService();

interface FileUploadProps {
    value: string;
    setValue: (url: string) => void;
    onLoadingChange?: (loading: boolean) => void;
    children?: (props: {
        loading: boolean;
        fileData: any;
        removeFile: any;
        error: string | null;
        inputRef: React.RefObject<HTMLInputElement>;
        handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    }) => ReactNode;
}

async function convertHeicIfNeeded(file: File): Promise<File> {
    const name = file.name || '';
    const type = file.type || '';
    const isHeic = /heic|heif/i.test(type) || /\.(heic|heif)$/i.test(name);

    if (!isHeic) return file;

    const blob = (await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9,
    })) as Blob;

    const newName = name.replace(/\.(heic|heif)$/i, '.jpg') || 'image.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Falha ao ler a imagem.'));
        };
        img.src = url;
    });
}

function replaceExt(name: string, ext: string) {
    return name.replace(/\.[a-z0-9]+$/i, '') + ext;
}

async function resizeIfNeeded(file: File, maxSide = 1400, jpegQuality = 0.85): Promise<File> {
    // só imagens
    if (!file.type.startsWith('image/')) return file;

    // se já é pequeno, evita mexer
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB <= 1.5) return file;

    const img = await loadImageFromFile(file);

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    const scale = Math.min(1, maxSide / Math.max(w, h));
    if (scale >= 1) return file;

    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = nw;
    canvas.height = nh;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, nw, nh);

    // Para avatar, JPEG costuma ser o melhor (menor). Mantém PNG se original era PNG.
    const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

    const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Falha ao gerar imagem comprimida.'))),
            outType,
            outType === 'image/jpeg' ? jpegQuality : undefined
        );
    });

    const outName =
        outType === 'image/png' ? replaceExt(file.name, '.png') : replaceExt(file.name, '.jpg');

    return new File([blob], outName, {
        type: outType,
        lastModified: Date.now(),
    });
}

export default function FileUploadUser({
    value,
    setValue,
    onLoadingChange,
    children,
}: FileUploadProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const previewUrlRef = useRef<string | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [fileData, setFileData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (value) {
            // se tinha preview blob, libera memória
            if (previewUrlRef.current?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrlRef.current);
                previewUrlRef.current = null;
            }
            setFileData({ url: value });
        } else {
            setFileData(null);
        }
    }, [value]);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
        return () => {
            if (previewUrlRef.current?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrlRef.current);
                previewUrlRef.current = null;
            }
        };
    }, []);

    const removeFile = () => {
        setValue('');
        setFileData(null);

        if (previewUrlRef.current?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        await handleUpload(file);

        // permite selecionar o mesmo arquivo de novo (iOS às vezes não dispara change)
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleUpload = async (rawFile: File) => {
        try {
            setLoading(true);
            setError(null);

            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                setError('Usuário não autenticado');
                return;
            }

            // ✅ 1) HEIC/HEIF → JPEG
            let file = await convertHeicIfNeeded(rawFile);

            // ✅ 2) Redimensiona/comprime se estiver grande (evita base64 gigante no iPhone/PWA)
            file = await resizeIfNeeded(file);

            // ✅ preview imediato (pra não parecer que "não funcionou")
            if (previewUrlRef.current?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
            previewUrlRef.current = URL.createObjectURL(file);
            setFileData({ url: previewUrlRef.current });

            // ✅ 3) Base64
            const base64Content = await convertFileToBase64(file);

            // ✅ 4) Upload (lastModifiedDate costuma dar ruim no iOS)
            const response = await service.upload(
                base64Content,
                file.name,
                file.size,
                file.type,
                new Date(file.lastModified),
                'profile'
            );

            setValue(`${urlFile}/${response.data._id}`);
        } catch (err: any) {
            console.error(err);
            setError(err?.message || 'Falha ao enviar arquivo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {children &&
                children({
                    loading,
                    fileData,
                    error,
                    handleChange,
                    removeFile,
                    inputRef,
                })}
        </div>
    );
}
